import React from 'react';

type SpinnerSize = 'sm' | 'md' | 'lg';

interface LoadingSpinnerProps {
  /** Optional text to display below the spinner */
  text?: string;
  /** Size of the spinner: 'sm' (20px), 'md' (40px), 'lg' (60px) */
  size?: SpinnerSize;
  /** If true, centers the spinner in the viewport with an overlay */
  fullscreen?: boolean;
}

const sizeMap: Record<SpinnerSize, number> = {
  sm: 20,
  md: 40,
  lg: 60,
};

const borderWidthMap: Record<SpinnerSize, number> = {
  sm: 2,
  md: 3,
  lg: 4,
};

// Keyframes need to be injected into the document for inline styles
const injectKeyframes = (): void => {
  const styleId = 'loading-spinner-keyframes';
  if (document.getElementById(styleId)) return;

  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = `
    @keyframes loadingSpinnerRotate {
      to { transform: rotate(360deg); }
    }
  `;
  document.head.appendChild(style);
};

const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  text,
  size = 'md',
  fullscreen = false,
}) => {
  // Inject keyframes on first render
  React.useEffect(() => {
    injectKeyframes();
  }, []);

  const dimension = sizeMap[size];
  const borderWidth = borderWidthMap[size];

  const spinnerStyle: React.CSSProperties = {
    width: dimension,
    height: dimension,
    border: `${borderWidth}px solid var(--bg-light, #4d4d4d)`,
    borderTopColor: 'var(--orange-primary, #ff8c00)',
    borderRadius: '50%',
    animation: 'loadingSpinnerRotate 1s linear infinite',
    boxSizing: 'border-box',
  };

  const textStyle: React.CSSProperties = {
    marginTop: size === 'sm' ? 8 : size === 'md' ? 12 : 16,
    fontSize: size === 'sm' ? '0.75rem' : size === 'md' ? '0.875rem' : '1rem',
    color: 'var(--text-secondary, #b0b0b0)',
    textAlign: 'center',
  };

  const inlineContainerStyle: React.CSSProperties = {
    display: 'inline-flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const fullscreenOverlayStyle: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(26, 26, 26, 0.85)',
    zIndex: 9999,
  };

  const containerStyle = fullscreen ? fullscreenOverlayStyle : inlineContainerStyle;

  return (
    <div style={containerStyle} role="status" aria-label={text || 'Loading'}>
      <div style={spinnerStyle} />
      {text && <div style={textStyle}>{text}</div>}
    </div>
  );
};

export default LoadingSpinner;
